import { actionTypes, opButtons, multiChartOptions } from './constants';
import { actionTypes as unitActionTypes } from '../../../UserProfileSettings/Units/constants';
import { actionTypes as scatterActionsV2 } from './SingleChartView/ScatterPlotV2/constants';
import { actionTypes as histogramActionsV2 } from './SingleChartView/HistogramV2/constants';
import { actionTypes as trendSectionActions } from './SingleChartView/Trend/TrendKPIList/constants';
import { actionTypes as trendCommonActions } from './SingleChartView/Trend/constants';
import { actionTypes as trendCommentsActions } from './SingleChartView/Trend/TrendComments/constants';
import { chartViews, actionTypes as FFTActionsV2 } from './VibrationFFTV2/constants';
import { commonActionTypes } from '../constants';
import { actionTypes as opHeaderActions } from './OperationalParametersHeader/constants';
import { getInitialMultiTrendCharts } from '../helpers';
import { getActionWithDefaultData } from 'helpers/actionHelper';
import {
    mapTrendKpiListV2
} from '../commonTrendHelpers';
import {
    getCommonTrendKPIs,
    isAssetCompatibleWithSelectedKPIs,
    isAssetCompatibleWithSelectedTrendKPIs,
    addKPISelection,
    addSelectOpTrendKPIData,
    removeSelectedOpTrendKpiData,
    removeSelectedTrendKpi,
    getAvailableKPIV2
} from './helpers';
import { apiCallStatus } from 'helpers/constants';
import { getKPISignalIndexFromList, removeSelectedIndexData } from '../../helpers';
import { actionTypes as internalTabsActionTypes } from './SingleChartView/Trend/InternalTabs/constants';
import { actionTypes as energySignalsActionTypes, energyViewModes } from './SingleChartView/Trend/EnergyRelatedSignalsList/constants';
import { setMaintainedLimitsData } from './SingleChartView/Trend/LimitSelector/helper';
import { setMaintainedTimestamps } from './VibrationFFTV2/helpers';
import { echartViews } from './VibrationFFTV2/Echart3D/constants';

const initialState = {
    activeTab: opButtons.trends,
    multiChart: multiChartOptions.SINGLE,
    charts: {
        single: {
            trend: [
                {
                    kpiList: [],
                    zoom: {
                        isZoomed: false,
                        from: null,
                        to: null
                    },
                    loadingStatus: null,
                    chartName: '',
                    chartID: 'op-trend-chart'
                }
            ]
        },
        multi: {
            trend: []
        }
    },
    scatterKpis: {
        data: [],
        loadingStatus: null,
    },
    scatter: {
        data: [],
        loadingStatus: null,
    },
    scatterV2Kpis: {
        data: [],
        loadingStatus: null,
    },
    scatterV2: {
        data: [],
        loadingStatus: null,
    },
    histogramKpis: {
        data: [],
        loadingStatus: null,
    },
    histogram: {
        data: [],
        loadingStatus: null,
    },
    histogramV2Kpis: {
        data: [],
        loadingStatus: null,
    },
    histogramV2: {
        data: [],
        loadingStatus: null,
    },
    measurementDownloadDialogParams: {
        showDownloadConfirmation: false,
        measurementTypeIds: '',
    },
    trendHealthStatuses: {
        data: [],
        loadingStatus: null,
    },
    measurementExport: {
        loadingStatus: undefined
    },
    FFT: {
        loadingStatus: null,
        data: [],
    },
    FFTV2: {
        loadingStatus: null,
        data: [],
    },
    FFT2DV2: {
        loadingStatus: null,
        data: [],
    },
    fftTimestampList: {
        loadingStatus: null,
        data: [],
    },
    availableKPIs: {
        trend: {
            loadingStatus: null,
            data: []
        }
    },
    preselectedKPIs: {
        assetType: null,
        single: {
            trend: []
        },
        multi: {
            trend: []
        }
    },
    loadMeasurementLoadingStatus: apiCallStatus.SUCCESS,
    loadMeasurementError: null,
    collectRawDataLoadingStatus: apiCallStatus.SUCCESS,
    collectRawDataError: null,
    downloadRawDataLoadingStatus: null,
    downloadRawDataError: null,
    selectedScatterKPI: null,
    selectedScatterV2KPI: null,
    selectedHistogramKPI: null,
    selectedHistogramV2KPI: null,
    selectedLimits: [],
    selectedLimitsAction: null,
    isDefaultLimitsSelected: false,
    // INTERNAL TABS
    isInternalTabLimitActive: false,
    clickedKpiConfig: null,
    maintainSelectedLimits: {},
    sensorLastCommand: {
        loadingStatus: null,
        data: {}
    },
    fftV2SelectedTimestamps: {},
    fft2DV2SelectedTimestamp: {},
    fftV2SelectedLegends: {},
    fft2DV2SelectedLegends: {},
    fftV2SelectedSpectrumRange: 'Default',
    fftV2ChartView: chartViews.FFT2D,
    fftV2SelectedXAxisUnit: null,
    bearingFaultSwitch: false,
    bearingFaultType: null,
    bearingFrequencies: {
        BSF: true,
        BPFI: true,
        BPFO: true
    },
    // ENERGY RELATED SIGNALS
    energyViewMode: energyViewModes.OVERALL,
    isTrendCommentsDrawerVisible: false,
    isTrendCommentsModalVisible: false,
    trendCommentsList: {
        data: [],
        error: null,
        loadingStatus: null
    },
    trendCommentsCreateEditLoadingStatus: null,
    trendCommentsCreateEditError: null,
    trendCommentsDeleteLoadingStatus: null,
    trendCommentsDeleteError: null,
    editCommentData: {},
    graphCommentTimestamp: null,
    selectedTrendCommentsList: [],
    speedHarmonicsSwitch: false,
    fftV2EchartView: echartViews.FREQUENCY,
    fftV2EchartLegend: null,
    fftV2EchartBandSettings: {
        bearingTypes: [],
        bsfType: true,
        bpfiType: true,
        bpfoType: true,
        cage: true,
        harmonics: true,
        bandsOnly: true,
        moreGraph: {
            maxAmplitude: null,
            toleranceBandWidth: 0.1,
            filterThreshold: null,
        }
    },
    frequencyRange: {
        min: null,
        max: null
    },
    limitFromSummary: null,
    internalActiveTab: null
};

// Helper function to handle common basic actions
const handleCommonActions = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_ACTIVE_TAB:
            return {
                ...state,
                activeTab: action.activeTab,
                multiChart: initialState.multiChart
            };
        case actionTypes.LOAD_MEASUREMENT:
        case actionTypes.LOAD_MEASUREMENT_LEGACY:
            return {
                ...state,
                loadMeasurementLoadingStatus: action.loadingStatus,
                loadMeasurementError: action.error ? {
                    statusCode: action.error?.response?.status || action.error?.statusCode,
                    message: action.error?.message
                } : null
            };
        case actionTypes.COLLECT_RAW_DATA:
        case actionTypes.COLLECT_RAW_DATA_LEGACY:
            return {
                ...state,
                collectRawDataLoadingStatus: action.loadingStatus,
                collectRawDataError: action.error ? {
                    statusCode: action.error?.response?.status || action.error?.statusCode,
                    message: action.error?.message
                } : null
            };
        case opHeaderActions.DOWNLOAD_RAW_DATA:
            return {
                ...state,
                downloadRawDataLoadingStatus: action.loadingStatus,
                downloadRawDataError: action.error ? {
                    statusCode: action.error?.response?.status || action.error?.statusCode,
                    message: action.error?.message
                } : null
            };
        case unitActionTypes.UPDATE_UNIT_SELECTION:
            return {
                ...state,
                availableKPIs: { ...initialState.availableKPIs },
                charts: initialState.charts,
                selectedLimits: initialState.selectedLimits,
                selectedLimitsAction: initialState.selectedLimitsAction,
                isDefaultLimitsSelected: initialState.isDefaultLimitsSelected,
                maintainSelectedLimits: initialState.maintainSelectedLimits,
            };
        case actionTypes.CLEAR_OPERATIONAL_PARAMETERS_API_ERRORS:
            return {
                ...state,
                trendCommentsList: { ...initialState.trendCommentsList },
                charts: initialState.charts,
                availableKPIs: { ...initialState.availableKPIs },
                scatterV2Kpis: { ...initialState.scatterV2Kpis },
                histogramV2Kpis: { ...initialState.histogramV2Kpis },
            };
        default:
            return null;
    }
};

// Helper function to handle trend actions (single and multi)
const handleTrendActions = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_ACTIVE_TREND_TAB:
            return {
                ...state,
                multiChart: action.multiChart
            };
        case trendSectionActions.SET_MEASUREMENT_DIALOG_PARAMS:
            return {
                ...state,
                measurementDownloadDialogParams: { ...action.measurementDownloadDialogParams }
            };
        case trendSectionActions.EXPORT_EXCEL_MEASUREMENT:
            return {
                ...state,
                measurementExport: { ...state.measurementExport, ...action }
            };
        case trendSectionActions.GET_ASSET_MEASUREMENT_HEALTH:
            return {
                ...state,
                trendHealthStatuses: getActionWithDefaultData(action, initialState.trendHealthStatuses)
            };
        case actionTypes.SET_MULTICHART_CONFIG:
            return {
                ...state,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    multi: {
                        ...state.preselectedKPIs.multi,
                        trend: action.data
                    }
                },
                charts: {
                    ...state.charts,
                    multi: {
                        ...state.charts.multi,
                        trend: getInitialMultiTrendCharts(action.data)
                    }
                }
            };
        case actionTypes.GET_MULTI_TREND_DATA:
            return {
                ...state,
                charts: {
                    ...state.charts,
                    multi: {
                        ...state.charts.multi,
                        trend: action.data
                    }
                }
            };
        case actionTypes.CLEAR_MULTICHART_DATA:
            return {
                ...state,
                charts: {
                    ...state.charts,
                    multi: { ...initialState.charts.multi }
                },
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    multi: { ...initialState.preselectedKPIs.multi }
                }
            };
        case actionTypes.CLEAR_UNMONITORED_CHART_DATA:
            return {
                ...state,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    single: {
                        ...state.preselectedKPIs.single,
                    }
                },
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                kpiList: [],
                            }
                        ]
                    }
                }
            };
        default:
            return null;
    }
};

// Helper function to handle trend KPI management actions
const buildCommentError = (action) =>
    action.error ? {
        statusCode: action.error?.response?.status || action.error?.statusCode,
        message: action.error?.message
    } : null;

const getTrendCommentListData = (action, currentData) =>
    action.loadingStatus === apiCallStatus.SUCCESS ? action.data : currentData;

const getSelectedDataPointTimestamp = (action) =>
    action.visible && action.selectedDataPointTimestamp ? action.selectedDataPointTimestamp : null;

const getEditCommentData = (action) =>
    action.visible && action.editCommentData ? action.editCommentData : {};

const getGraphCommentTimestamp = (action) =>
    action.visible && action.graphCommentTimestamp ? action.graphCommentTimestamp : null;

const handleTrendCommentsActions = (state, action) => {
    switch (action.type) {
        case trendCommentsActions.SET_TREND_COMMENTS_DRAWER_VISIBLE:
            return {
                ...state,
                isTrendCommentsDrawerVisible: action.visible,
                selectedDataPointTimestamp: getSelectedDataPointTimestamp(action)
            };
        case trendCommentsActions.SET_TREND_COMMENTS_MODAL_VISIBLE:
            return {
                ...state,
                isTrendCommentsModalVisible: action.visible,
                trendCommentsCreateEditError: action.visible ? null : state.trendCommentsCreateEditError,
                editCommentData: getEditCommentData(action),
                graphCommentTimestamp: getGraphCommentTimestamp(action)
            };
        case trendCommentsActions.GET_TREND_COMMENTS_LIST_SETTINGS:
            return {
                ...state,
                trendCommentsList: {
                    ...state.trendCommentsList,
                    ...action,
                    data: getTrendCommentListData(action, state.trendCommentsList?.data)
                },
                selectedTrendCommentsList: getTrendCommentListData(action, state.trendCommentsList?.data)
            };
        case trendCommentsActions.CREATE_AND_EDIT_TREND_COMMENT_SETTINGS:
            return {
                ...state,
                trendCommentsCreateEditLoadingStatus: action.loadingStatus,
                trendCommentsCreateEditError: buildCommentError(action),
                isTrendCommentsModalVisible: action.loadingStatus === apiCallStatus.SUCCESS ? false : state.isTrendCommentsModalVisible,
            };
        case trendCommentsActions.CLEAR_CREATE_EDIT_TREND_COMMENT_ERROR:
            return {
                ...state,
                trendCommentsCreateEditLoadingStatus: apiCallStatus.IDLE,
                trendCommentsCreateEditError: null
            };
        case trendCommentsActions.DELETE_TREND_COMMENT_SETTINGS:
            return {
                ...state,
                trendCommentsDeleteLoadingStatus: action.loadingStatus,
                trendCommentsDeleteError: buildCommentError(action)
            };
        case trendCommentsActions.CLEAR_DELETE_TREND_COMMENT_ERROR:
            return {
                ...state,
                trendCommentsDeleteLoadingStatus: apiCallStatus.IDLE,
                trendCommentsDeleteError: null
            };
        case trendCommentsActions.RESET_TREND_COMMENT_SELECTION:
            return {
                ...state,
                selectedDataPointTimestamp: null
            };
        default:
            return null;
    }
};

const handleTrendKPIActions = (state, action) => {
    switch (action.type) {
        case commonActionTypes.GET_ASSET_TREND_KPIS_V2: {
            const commonTrendKPIs = action.loadingStatus !== apiCallStatus.LOADING && action?.data ?
                getCommonTrendKPIs(action.data, state.preselectedKPIs.single.trend) : state.preselectedKPIs.single.trend;
            return {
                ...state,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    single: {
                        ...state.preselectedKPIs.single,
                        trend: commonTrendKPIs
                    }
                },
                availableKPIs: {
                    trend: {
                        loadingStatus: action.loadingStatus,
                        data: mapTrendKpiListV2(action.data),
                        error: action?.error
                    }
                }
            };
        }
        case trendCommonActions.ADD_TREND_KPI_SELECTION:
            return {
                ...state,
                selectedLimits: initialState.selectedLimits,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    single: {
                        ...state.preselectedKPIs.single,
                        trend: addKPISelection([...state.preselectedKPIs.single.trend || []], action.selectedTrendKPI.timeseriesKey)
                    }
                }
            };
        case trendCommonActions.GET_TREND_KPI_LIST_SELECTION:
            return {
                ...state,
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                kpiList: action.data,
                            }
                        ]
                    }
                }
            };
        case trendCommonActions.ADD_SELECTED_TREND_KPI_DATA:
        case trendCommonActions.RELOAD_ALL_SELECTED_TREND_KPI:
            return {
                ...state,
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                kpiList: addSelectOpTrendKPIData([...state.charts.single.trend[0].kpiList || []], action.newKpiData, action.selectedKpiKeys),
                                zoom: action.trendChartZoom
                            }
                        ]
                    }
                }
            };
        case trendCommonActions.REMOVE_TREND_KPI_SELECTION:
            return {
                ...state,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    single: {
                        ...state.preselectedKPIs.single,
                        trend: removeSelectedTrendKpi([...state.preselectedKPIs.single.trend], action.unselectedTrendKPI)
                    }
                },
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                kpiList: removeSelectedOpTrendKpiData([...state.charts.single.trend[0].kpiList], action.unselectedTrendKPI),
                            }
                        ]
                    }
                }
            };
        case trendCommonActions.REMOVE_TREND_KPI_SELECTION_LEGEND: {
            const preselectedKPIIndex = getKPISignalIndexFromList(state.preselectedKPIs.single.trend, state.availableKPIs.trend.data, action.signalIndex);
            const selectedKPIIndex = getKPISignalIndexFromList(state.charts.single.trend[0].kpiList, state.availableKPIs.trend.data, action.signalIndex);
            return {
                ...state,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    single: {
                        ...state.preselectedKPIs.single,
                        trend: removeSelectedIndexData([...state.preselectedKPIs.single.trend], preselectedKPIIndex)
                    }
                },
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                kpiList: removeSelectedIndexData([...state.charts.single.trend[0].kpiList], selectedKPIIndex),
                            }
                        ]
                    }
                }
            };
        }
        case trendCommonActions.RESET_TREND_KPI_SELECTION:
            return {
                ...state,
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    single: {
                        ...state.preselectedKPIs.single,
                        trend: []
                    }
                },
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                kpiList: [],
                            }
                        ]
                    }
                }
            };
        case trendCommonActions.SET_ZOOMED:
            return {
                ...state,
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                zoom: {
                                    isZoomed: action.isTrendChartZoomed,
                                    from: action.from,
                                    to: action.to
                                }
                            }
                        ]
                    }
                }
            };
        case trendCommonActions.GET_ASSET_TREND_DATA:
            return {
                ...state,
                charts: {
                    ...state.charts,
                    single: {
                        ...state.charts.single,
                        trend: [
                            {
                                ...state.charts.single.trend[0],
                                loadingStatus: action.loadingStatus,
                                error: action.loadingStatus === apiCallStatus.ERROR ? action.error : null
                            }
                        ]
                    }
                }
            };
        case trendCommonActions.SET_SELECTED_LIMITS:
            return {
                ...state,
                selectedLimits: action.data
            };

        case trendCommonActions.SET_SELECTED_LIMITS_ACTION:
            return {
                ...state,
                selectedLimitsAction: action.data
            };
        case trendCommonActions.SET_IS_DEFAULT_SELECTED_LIMITS:
            return {
                ...state,
                isDefaultLimitsSelected: action.data
            };
        case trendCommonActions.MAINTAIN_LIMITS_BETWEEN_SIGNALS_AND_ASSETS: {
            const modifiedLimitsData = setMaintainedLimitsData(state, action);
            return {
                ...state,
                maintainSelectedLimits: modifiedLimitsData
            };
        }
        case energySignalsActionTypes.SET_ENERGY_VIEW_MODE:
            return {
                ...state,
                energyViewMode: action.payload
            };
        default:
            return null;
    }
};

// Helper function to handle scatter plot actions (V1 and V2)
const handleScatterActions = (state, action) => {
    switch (action.type) {
        case scatterActionsV2.GET_ASSET_SCATTER_KPIS_V2: {
            const availableKPI = action.loadingStatus !== apiCallStatus.LOADING && action?.data?.length ?
                getAvailableKPIV2(action?.data, state.selectedScatterV2KPI, true) : state.selectedScatterV2KPI;
            return {
                ...state,
                scatterV2Kpis: getActionWithDefaultData(action, initialState.scatterV2Kpis),
                selectedScatterV2KPI: availableKPI
            };
        }
        case scatterActionsV2.GET_SCATTER_V2:
            return {
                ...state,
                scatterV2: getActionWithDefaultData(action, initialState.scatterV2)
            };
        case scatterActionsV2.SET_SCATTER_KPI_V2:
            return {
                ...state,
                selectedScatterV2KPI: action.scatterSelectedKpi
            };
        case scatterActionsV2.REMOVE_TREND_KPI_SELECTION_LEGEND_SCATTER_V2: {
            return {
                ...state,
                selectedScatterV2KPI: null
            };
        }
        default:
            return null;
    }
};

// Helper function to handle histogram actions (V1 and V2)
const handleHistogramActions = (state, action) => {
    switch (action.type) {
        case histogramActionsV2.GET_ASSET_HISTOGRAM_KPIS_V2: {
            const availableKPI = action.loadingStatus !== apiCallStatus.LOADING && action?.data?.length ?
                getAvailableKPIV2(action?.data, state.selectedHistogramV2KPI) : state.selectedHistogramV2KPI;
            return {
                ...state,
                histogramV2Kpis: getActionWithDefaultData(action, initialState.histogramV2Kpis),
                selectedHistogramV2KPI: availableKPI
            };
        }
        case histogramActionsV2.GET_HISTOGRAM_V2:
            return {
                ...state,
                histogramV2: getActionWithDefaultData(action, initialState.histogramV2)
            };
        case histogramActionsV2.SET_HISTOGRAM_KPI_V2:
            return {
                ...state,
                selectedHistogramV2KPI: action.histogramSelectedKpi
            };
        case histogramActionsV2.REMOVE_TREND_KPI_SELECTION_LEGEND_HISTOGRAM_V2:
            return {
                ...state,
                selectedHistogramV2KPI: null
            };
        default:
            return null;
    }
};

// Helper function to handle FFT actions (V1 and V2)
const handleFFTActions = (state, action) => {
    switch (action.type) {
        // case FFTActions.GET_FFT:
        //     return {
        //         ...state,
        //         FFT: { ...action }
        //     };
        case FFTActionsV2.GET_FFT_V2_ACTION:
            return {
                ...state,
                FFTV2: { ...action }
            };
        case FFTActionsV2.GET_2D_FFT_V2_ACTION:
            return {
                ...state,
                FFT2DV2: { ...action }
            };
        case FFTActionsV2.GET_FFT_V2_TIMESTAMP_LIST_ACTION:
            return {
                ...state,
                fftTimestampList: { ...action }
            };
        case FFTActionsV2.SET_FFT_V2_SELECTED_TIMESTAMPS: {
            const modifiedTimestamps = setMaintainedTimestamps(state.fftV2SelectedTimestamps, action);
            return {
                ...state,
                fftV2SelectedTimestamps: modifiedTimestamps
            };
        }
        case FFTActionsV2.SET_2D_FFT_V2_SELECTED_TIMESTAMP: {
            const modifiedTimestamps = setMaintainedTimestamps(state.fft2DV2SelectedTimestamp, action);
            return {
                ...state,
                fft2DV2SelectedTimestamp: modifiedTimestamps
            };
        }
        case FFTActionsV2.SET_FFT_V2_SELECTED_LEGENDS:
            return {
                ...state,
                fftV2SelectedLegends: action.selectedLegends
            };
        case FFTActionsV2.SET_2D_FFT_V2_SELECTED_LEGENDS:
            return {
                ...state,
                fft2DV2SelectedLegends: action.selectedLegends
            };
        case FFTActionsV2.SET_FFT_V2_SELECTED_SPECTRUM_RANGE:
            return {
                ...state,
                fftV2SelectedSpectrumRange: action.spectrumRange
            };
        case FFTActionsV2.SET_FFT_V2_SELECTED_CHART_VIEW:
            return {
                ...state,
                fftV2ChartView: action.chartView
            };
        case FFTActionsV2.SET_FFT_V2_SELECTED_XAXIS_UNIT:
            return {
                ...state,
                fftV2SelectedXAxisUnit: action.xAxisUnit
            };
        case FFTActionsV2.SET_FFT_V2_CHART_LOADER:
            return {
                ...state,
                FFTV2: {
                    ...state.FFTV2,
                    loadingStatus: action.loadingStatus,
                },
            };
        case FFTActionsV2.SET_FFT_BEARING_FAULT_SWITCH:
            return {
                ...state,
                bearingFaultSwitch: action.status,
                speedHarmonicsSwitch: action.isToggle ? false : state.speedHarmonicsSwitch
            };
        case FFTActionsV2.SET_FFT_BEARING_FAULT_TYPE:
            return {
                ...state,
                bearingFaultType: action.bearingType
            };
        case FFTActionsV2.SET_FFT_BEARING_FAULT_FREQUENCIES:
            return {
                ...state,
                bearingFrequencies: action.frequencies
            };
        case FFTActionsV2.SET_FFT_SPEED_HARMONICS_SWITCH:
            return {
                ...state,
                speedHarmonicsSwitch: action.status,
                bearingFaultSwitch: action.isToggle ? false : state.bearingFaultSwitch
            };
        case FFTActionsV2.SET_FFT_V2_ECHART_VIEW:
            return {
                ...state,
                fftV2EchartView: action.echartView
            };
        case FFTActionsV2.SET_FFT_V2_ECHART_LEGEND:
            return {
                ...state,
                fftV2EchartLegend: action.echartLegend
            };
        case FFTActionsV2.SET_FFT_V2_ECHART_BAND_SETTINGS:
            return {
                ...state,
                fftV2EchartBandSettings: action.bandSettings
            };
        case FFTActionsV2.SET_FFT_V2_ECHART_FREQUENCY_RANGE:
            return {
                ...state,
                frequencyRange: action.frequencyRange
            };
        default:
            return null;
    }
};

// Helper function to handle internal tabs and miscellaneous actions
const handleInternalTabsActions = (state, action) => {
    switch (action.type) {
        case internalTabsActionTypes.SET_SHOW_INTERNAL_TAB_LIMIT_SETTING:
            return {
                ...state,
                isInternalTabLimitActive: action.data
            };
        case internalTabsActionTypes.SET_CLICKED_CONFIG_KPI:
            return {
                ...state,
                clickedKpiConfig: action.data
            };
        case opHeaderActions.GET_SMART_SENSOR_LAST_COMMAND:
            return {
                ...state,
                sensorLastCommand: { ...action }
            };
        case internalTabsActionTypes.SET_INTERNAL_ACTIVE_TAB:
            return {
                ...state,
                internalActiveTab: action.data
            };
        default:
            return null;
    }
};

// Helper function to handle asset selection actions
const handleAssetActions = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_SELECTED_ASSET:
            return {
                ...state,
                multiChart: initialState.multiChart,
                charts: initialState.charts,
                measurementExport: initialState.measurementExport,
                measurementDownloadDialogParams: initialState.measurementDownloadDialogParams,
                trendHealthStatuses: initialState.trendHealthStatuses,
                scatterKpis: { ...initialState.scatterKpis },
                scatter: { ...initialState.scatter },
                scatterV2Kpis: { ...initialState.scatterV2Kpis },
                scatterV2: { ...initialState.scatterV2 },
                histogramKpis: { ...initialState.histogramKpis },
                histogram: { ...initialState.histogram },
                histogramV2Kpis: { ...initialState.histogramV2Kpis },
                histogramV2: { ...initialState.histogramV2 },
                availableKPIs: { ...initialState.availableKPIs },
                preselectedKPIs: {
                    ...state.preselectedKPIs,
                    assetType: action.asset?.assetType,
                    single: {
                        ...state.preselectedKPIs.single,
                        trend: state.activeTab === opButtons.trends &&
                            isAssetCompatibleWithSelectedTrendKPIs(action.asset, state.preselectedKPIs.assetType) ?
                            state.preselectedKPIs.single.trend : initialState.preselectedKPIs.single.trend,
                    },
                    multi: { ...initialState.preselectedKPIs.multi }
                },
                selectedScatterKPI: state.activeTab === opButtons.scatterPlot &&
                    isAssetCompatibleWithSelectedKPIs(action.asset, [state.selectedScatterKPI]) ?
                    state.selectedScatterKPI : initialState.selectedScatterKPI,
                selectedHistogramKPI: state.activeTab === opButtons.histogram &&
                    isAssetCompatibleWithSelectedKPIs(action.asset, [state.selectedHistogramKPI]) ?
                    state.selectedHistogramKPI : initialState.selectedHistogramKPI,
                selectedLimits: initialState.selectedLimits,
                selectedLimitsAction: initialState.selectedLimitsAction,
                isDefaultLimitsSelected: initialState.isDefaultLimitsSelected,
                isInternalTabLimitActive: initialState.isInternalTabLimitActive,
                internalActiveTab: initialState.internalActiveTab,
                fftV2SelectedLegends: initialState.fftV2SelectedLegends,
                fft2DV2SelectedLegends: initialState.fft2DV2SelectedLegends,
                fftV2SelectedSpectrumRange: initialState.fftV2SelectedSpectrumRange,
                fftV2ChartView: initialState.fftV2ChartView,
                fftV2SelectedXAxisUnit: initialState.fftV2SelectedXAxisUnit,
                FFT2DV2: { ...initialState.FFT2DV2 },
                FFTV2: { ...initialState.FFTV2 },
                fftV2EchartView: initialState.fftV2EchartView,
                fftV2EchartLegend: initialState.fftV2EchartLegend,
                fftV2EchartBandSettings: { ...initialState.fftV2EchartBandSettings },
                frequencyRange: { ...initialState.frequencyRange }
            };

        case trendCommonActions.SET_LIMIT_FROM_SUMMARY_TAB:
            return {
                ...state,
                limitFromSummary: action.payload
            };
        default:
            return null;
    }
};

// Main reducer function
const operationalParametersReducer = (state = initialState, action) => {
    // common actions first
    const commonResult = handleCommonActions(state, action);
    if (commonResult) { return commonResult; }

    // trend actions
    const trendResult = handleTrendActions(state, action);
    if (trendResult) { return trendResult; }

    // trend KPI actions
    const trendKPIResult = handleTrendKPIActions(state, action);
    if (trendKPIResult) { return trendKPIResult; }

    // trend comments actions
    const trendCommentsResult = handleTrendCommentsActions(state, action);
    if (trendCommentsResult) { return trendCommentsResult; }

    // scatter actions
    const scatterResult = handleScatterActions(state, action);
    if (scatterResult) { return scatterResult; }

    // histogram actions
    const histogramResult = handleHistogramActions(state, action);
    if (histogramResult) { return histogramResult; }

    // FFT actions
    const fftResult = handleFFTActions(state, action);
    if (fftResult) { return fftResult; }

    // internal tabs and miscellaneous actions
    const internalTabsResult = handleInternalTabsActions(state, action);
    if (internalTabsResult) { return internalTabsResult; }

    // asset selection actions
    const assetResult = handleAssetActions(state, action);
    if (assetResult) { return assetResult; }

    return state;
};

export default operationalParametersReducer;
